import * as React from "react";
import { 
  Activity, ArrowLeft, Search, AlertTriangle, CheckCircle2, 
  XCircle, TrendingUp, TrendingDown, Loader2, Mail, Clock,
  Bell, Plus, X, RefreshCw, HelpCircle, Info, Calendar
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { ZScoreInfoPage } from "./ZScoreInfoPage";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper to format dates for input
const formatDateForInput = (date) => {
  return date.toISOString().split('T')[0];
};

// Get default date ranges
const getDefaultDates = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const sixMonthsAgo = new Date(today);
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
  return {
    baselineStart: formatDateForInput(sixMonthsAgo),
    baselineEnd: formatDateForInput(today),
    analysisStart: formatDateForInput(thirtyDaysAgo),
    analysisEnd: formatDateForInput(today)
  };
};

const StatusIcon = ({ status }) => {
  if (status === "CRITICAL") {
    return <XCircle className="h-4 w-4 text-red-500" />;
  } else if (status === "WARNING") {
    return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  }
  return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
};

const MetricCell = ({ metric, label }) => {
  const getBgColor = () => {
    if (metric.status === "CRITICAL") return "bg-red-50";
    if (metric.status === "WARNING") return "bg-amber-50";
    return "";
  };

  const getZScoreColor = () => {
    if (metric.status === "CRITICAL") return "text-red-600 font-semibold";
    if (metric.status === "WARNING") return "text-amber-600 font-semibold";
    return "text-slate-500";
  };

  const getZScoreExplanation = (z) => {
    const absZ = Math.abs(z);
    if (absZ > 3) return "Muy alejado de lo normal (>3σ)";
    if (absZ > 2) return "Significativamente diferente (>2σ)";
    if (absZ > 1) return "Ligeramente diferente (>1σ)";
    return "Dentro del rango normal";
  };

  const getDirectionText = (z) => {
    if (z > 0) return "por encima";
    if (z < 0) return "por debajo";
    return "igual a";
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`px-2 py-1 rounded ${getBgColor()} cursor-help transition-all hover:ring-2 hover:ring-purple-200`}>
            <div className="flex items-center gap-1">
              <StatusIcon status={metric.status} />
              <span className="text-sm font-medium">
                {typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value}
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="text-xs space-y-2">
            <p className="font-semibold text-sm border-b pb-1">{label}</p>
            <div className="space-y-1">
              <p><span className="text-slate-400">Valor actual:</span> <span className="font-medium">{metric.value.toLocaleString()}</span></p>
              <p className={getZScoreColor()}>
                <span className="text-slate-400">Z-Score:</span> {metric.z_score.toFixed(3)}
              </p>
            </div>
            <div className="pt-1 border-t">
              <p className="text-slate-300 italic">
                {Math.abs(metric.z_score).toFixed(1)} desviaciones estándar {getDirectionText(metric.z_score)} de la media
              </p>
              <p className="mt-1">
                {metric.status === "CRITICAL" && "🔴 "}
                {metric.status === "WARNING" && "🟡 "}
                {metric.status === "NORMAL" && "🟢 "}
                {getZScoreExplanation(metric.z_score)}
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const SummaryCard = ({ title, value, icon: Icon, color, subtext, tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Card className={`border-l-4 ${color} cursor-help transition-all hover:shadow-md`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{title}</p>
                <p className="text-2xl font-bold">{value}</p>
                {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
              </div>
              <Icon className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </TooltipTrigger>
      {tooltip && (
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      )}
    </Tooltip>
  </TooltipProvider>
);

export const AnomalyDetectionView = ({ onBack }) => {
  const [clients, setClients] = React.useState([]);
  const [selectedClient, setSelectedClient] = React.useState("");
  const [analysisResult, setAnalysisResult] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingClients, setIsLoadingClients] = React.useState(true);
  const [showInfoPage, setShowInfoPage] = React.useState(false);
  
  // Date ranges
  const defaultDates = React.useMemo(() => getDefaultDates(), []);
  const [baselineStart, setBaselineStart] = React.useState(defaultDates.baselineStart);
  const [baselineEnd, setBaselineEnd] = React.useState(defaultDates.baselineEnd);
  const [analysisStart, setAnalysisStart] = React.useState(defaultDates.analysisStart);
  const [analysisEnd, setAnalysisEnd] = React.useState(defaultDates.analysisEnd);
  
  // Alert configuration
  const [showAlertConfig, setShowAlertConfig] = React.useState(false);

  // Activity tracking (with duplicate prevention)
  const hasTrackedView = React.useRef(false);
  const trackActivity = React.useCallback(async (action, details = null) => {
    try {
      await axios.post(`${API}/track-activity`, {
        action,
        module: "anomaly",
        details
      }, { withCredentials: true });
    } catch (e) { /* silent */ }
  }, []);

  // Track view on mount (only once)
  React.useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackActivity("view");
    }
  }, [trackActivity]);
  const [alertName, setAlertName] = React.useState("");
  const [scheduleInterval, setScheduleInterval] = React.useState("daily");
  const [alertOnWarning, setAlertOnWarning] = React.useState(false);
  const [notificationEmails, setNotificationEmails] = React.useState([]);
  const [emailInput, setEmailInput] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [isCreatingAlert, setIsCreatingAlert] = React.useState(false);
  
  // Existing alerts
  const [existingAlerts, setExistingAlerts] = React.useState([]);

  // Quick date presets - Baseline
  const applyBaselinePreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch(preset) {
      case "last_12_months":
        start = new Date(today);
        start.setMonth(start.getMonth() - 12);
        end = today;
        break;
      case "last_6_months":
        start = new Date(today);
        start.setMonth(start.getMonth() - 6);
        end = today;
        break;
      case "last_3_months":
        start = new Date(today);
        start.setMonth(start.getMonth() - 3);
        end = today;
        break;
      case "ytd":
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case "last_year":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case "q4_last_year":
        start = new Date(today.getFullYear() - 1, 9, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      default:
        return;
    }
    
    setBaselineStart(formatDateForInput(start));
    setBaselineEnd(formatDateForInput(end));
  };

  // Quick date presets - Analysis Period
  const applyAnalysisPreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch(preset) {
      case "yesterday":
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        break;
      case "last_7_days":
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        end = today;
        break;
      case "last_14_days":
        start = new Date(today);
        start.setDate(start.getDate() - 14);
        end = today;
        break;
      case "last_30_days":
        start = new Date(today);
        start.setDate(start.getDate() - 30);
        end = today;
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "last_90_days":
        start = new Date(today);
        start.setDate(start.getDate() - 90);
        end = today;
        break;
      default:
        return;
    }
    
    setAnalysisStart(formatDateForInput(start));
    setAnalysisEnd(formatDateForInput(end));
  };

  React.useEffect(() => {
    fetchClients();
    fetchExistingAlerts();
  }, []);

  // Show info page if requested
  if (showInfoPage) {
    return <ZScoreInfoPage onBack={() => setShowInfoPage(false)} />;
  }

  const fetchClients = async () => {
    try {
      setIsLoadingClients(true);
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Error al cargar clientes", {
        description: "No se pudieron obtener los clientes de Databricks"
      });
    } finally {
      setIsLoadingClients(false);
    }
  };

  const fetchExistingAlerts = async () => {
    try {
      const response = await axios.get(`${API}/anomaly-alerts`);
      setExistingAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const analyzeClient = async () => {
    if (!selectedClient) return;
    
    try {
      setIsLoading(true);
      setAnalysisResult(null);

      // Track query
      trackActivity("query", { 
        filters: { client: selectedClient, baseline: `${baselineStart} to ${baselineEnd}`, analysis: `${analysisStart} to ${analysisEnd}` }
      });

      const response = await axios.post(`${API}/anomalies/analyze`, {
        client_name: selectedClient,
        baseline_start: baselineStart,
        baseline_end: baselineEnd,
        analysis_start: analysisStart,
        analysis_end: analysisEnd
      });
      setAnalysisResult(response.data);
      
      if (response.data.anomalies.length === 0) {
        toast.info("Sin anomalías detectadas", {
          description: "Todas las métricas están dentro de los rangos normales"
        });
      } else if (response.data.critical_count > 0) {
        toast.warning(`${response.data.critical_count} anomalías críticas detectadas`, {
          description: "Revisa los detalles en la tabla"
        });
      }
    } catch (error) {
      console.error("Error analyzing:", error);
      toast.error("Error al analizar", {
        description: error.response?.data?.detail || "No se pudo ejecutar el análisis"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    
    if (!validateEmail(email)) {
      setEmailError("Formato de email inválido");
      return;
    }
    
    if (notificationEmails.includes(email)) {
      setEmailError("Este email ya está agregado");
      return;
    }
    
    setNotificationEmails([...notificationEmails, email]);
    setEmailInput("");
    setEmailError("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setNotificationEmails(notificationEmails.filter(e => e !== emailToRemove));
  };

  const createAlert = async () => {
    if (!alertName.trim() || !selectedClient) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    try {
      setIsCreatingAlert(true);
      const response = await axios.post(`${API}/anomaly-alerts`, {
        alert_name: alertName.trim(),
        client_name: selectedClient,
        notification_emails: notificationEmails,
        schedule_interval: scheduleInterval,
        alert_on_warning: alertOnWarning
      });

      if (response.data.notification_status === "configured") {
        toast.success("Alerta de anomalías creada", {
          description: "Las notificaciones por email están configuradas automáticamente"
        });
      } else {
        toast.success("Alerta creada", {
          description: `La alerta "${alertName}" está activa`
        });
      }

      // Reset form
      setAlertName("");
      setNotificationEmails([]);
      setShowAlertConfig(false);
      fetchExistingAlerts();
      
    } catch (error) {
      console.error("Error creating alert:", error);
      toast.error("Error al crear alerta", {
        description: error.response?.data?.detail || "Intenta de nuevo"
      });
    } finally {
      setIsCreatingAlert(false);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/anomaly-alerts/${alertId}`);
      toast.success("Alerta eliminada");
      fetchExistingAlerts();
    } catch (error) {
      toast.error("Error al eliminar alerta");
    }
  };

  const formatSchedule = (interval) => {
    const labels = {
      "1h": "Cada hora",
      "6h": "Cada 6 horas",
      "daily": "Diario",
      "weekly": "Semanal"
    };
    return labels[interval] || interval;
  };

  return (
    <div className="min-h-screen bg-[#e8eef3]">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-800">Detección de Anomalías</h1>
                  <p className="text-xs text-slate-500">Análisis Z-Score de métricas</p>
                </div>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowInfoPage(true)}
              className="gap-2"
            >
              <HelpCircle className="h-4 w-4" />
              ¿Cómo funciona?
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Client Selection & Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Analizar Cliente</CardTitle>
            <CardDescription>
              Selecciona un cliente y configura los períodos para el análisis de anomalías Z-Score
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Client Selection */}
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select 
                value={selectedClient} 
                onValueChange={setSelectedClient}
                disabled={isLoadingClients}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder={isLoadingClients ? "Cargando clientes..." : "Seleccionar cliente"} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.cliente} value={client.cliente}>
                      {client.cliente} ({client.account_count} cuentas)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Ranges */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Baseline Period */}
              <div className="space-y-3 p-4 bg-purple-50/50 rounded-lg border border-purple-100">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-purple-500" />
                  <Label className="text-purple-700 font-medium">Período Histórico (Baseline)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3.5 w-3.5 text-purple-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs max-w-xs">
                          Período usado para calcular la media y desviación estándar (parámetros del Z-Score).
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                
                {/* Baseline Presets */}
                <div className="flex flex-wrap gap-1.5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyBaselinePreset("last_12_months")}
                  >
                    12 meses
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyBaselinePreset("last_6_months")}
                  >
                    6 meses
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyBaselinePreset("last_3_months")}
                  >
                    3 meses
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyBaselinePreset("ytd")}
                  >
                    YTD
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-purple-100 hover:text-purple-700"
                    onClick={() => applyBaselinePreset("last_year")}
                  >
                    Año pasado
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Desde</Label>
                    <Input 
                      type="date" 
                      value={baselineStart}
                      onChange={(e) => setBaselineStart(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Hasta</Label>
                    <Input 
                      type="date" 
                      value={baselineEnd}
                      onChange={(e) => setBaselineEnd(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Analysis Period */}
              <div className="space-y-3 p-4 bg-pink-50/50 rounded-lg border border-pink-100">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-pink-500" />
                  <Label className="text-pink-700 font-medium">Período de Análisis</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3.5 w-3.5 text-pink-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs max-w-xs">
                          Días que quieres evaluar para detectar anomalías respecto al baseline.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                
                {/* Analysis Presets */}
                <div className="flex flex-wrap gap-1.5">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-pink-100 hover:text-pink-700"
                    onClick={() => applyAnalysisPreset("yesterday")}
                  >
                    Ayer
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-pink-100 hover:text-pink-700"
                    onClick={() => applyAnalysisPreset("last_7_days")}
                  >
                    7 días
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-pink-100 hover:text-pink-700"
                    onClick={() => applyAnalysisPreset("last_14_days")}
                  >
                    14 días
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-pink-100 hover:text-pink-700"
                    onClick={() => applyAnalysisPreset("last_30_days")}
                  >
                    30 días
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-pink-100 hover:text-pink-700"
                    onClick={() => applyAnalysisPreset("this_month")}
                  >
                    Este mes
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs bg-white hover:bg-pink-100 hover:text-pink-700"
                    onClick={() => applyAnalysisPreset("last_month")}
                  >
                    Mes pasado
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Desde</Label>
                    <Input 
                      type="date" 
                      value={analysisStart}
                      onChange={(e) => setAnalysisStart(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Hasta</Label>
                    <Input 
                      type="date" 
                      value={analysisEnd}
                      onChange={(e) => setAnalysisEnd(e.target.value)}
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <Button 
                variant="outline"
                onClick={() => setShowAlertConfig(!showAlertConfig)}
                disabled={!selectedClient}
              >
                <Bell className="h-4 w-4 mr-2" />
                Configurar Alerta
              </Button>
              <Button 
                onClick={analyzeClient}
                disabled={!selectedClient || isLoading}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Analizar Anomalías
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alert Configuration Panel */}
        {showAlertConfig && selectedClient && (
          <Card className="border-purple-200 bg-purple-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5 text-purple-500" />
                Configurar Alerta Programada
              </CardTitle>
              <CardDescription>
                Configura notificaciones automáticas cuando se detecten anomalías
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la Alerta</Label>
                  <Input
                    placeholder="Ej: Anomalías Banco GyT"
                    value={alertName}
                    onChange={(e) => setAlertName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    Frecuencia de Ejecución
                  </Label>
                  <Select value={scheduleInterval} onValueChange={setScheduleInterval}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1h">Cada hora</SelectItem>
                      <SelectItem value="6h">Cada 6 horas</SelectItem>
                      <SelectItem value="daily">Diario</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-3 w-3" />
                  Emails para Notificaciones
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddEmail())}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={handleAddEmail}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                {notificationEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {notificationEmails.map((email) => (
                      <Badge key={email} variant="secondary" className="pr-1">
                        {email}
                        <button onClick={() => handleRemoveEmail(email)} className="ml-1 p-0.5 rounded-full hover:bg-slate-300">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <Label className="text-sm font-medium">Incluir alertas de nivel WARNING</Label>
                  <p className="text-xs text-slate-500">Además de CRITICAL, notificar también cuando haya anomalías de nivel WARNING</p>
                </div>
                <Switch checked={alertOnWarning} onCheckedChange={setAlertOnWarning} />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAlertConfig(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={createAlert}
                  disabled={isCreatingAlert || !alertName.trim()}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  {isCreatingAlert ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creando...</>
                  ) : (
                    "Crear Alerta"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analysis Results */}
        {analysisResult && (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4">
              <SummaryCard
                title="Cuentas Analizadas"
                value={analysisResult.total_accounts_analyzed}
                icon={Activity}
                color="border-l-slate-400"
                tooltip="Número de cuentas de Google Ads del cliente que tienen datos suficientes para el análisis estadístico."
              />
              <SummaryCard
                title="Críticas"
                value={analysisResult.critical_count}
                icon={XCircle}
                color="border-l-red-500"
                subtext={analysisResult.critical_count > 0 ? "Requiere atención" : ""}
                tooltip="Anomalías con Z-Score > 3: Valores que se desvían más de 3 desviaciones estándar de la media. Ocurre en solo 0.3% de los casos normales."
              />
              <SummaryCard
                title="Advertencias"
                value={analysisResult.warning_count}
                icon={AlertTriangle}
                color="border-l-amber-500"
                tooltip="Anomalías con Z-Score entre 2 y 3: Valores significativamente diferentes de lo normal. Vale la pena investigar."
              />
              <SummaryCard
                title="Normales"
                value={analysisResult.normal_count}
                icon={CheckCircle2}
                color="border-l-emerald-500"
                tooltip="Registros con todas las métricas dentro del rango normal (Z-Score ≤ 2). Representan comportamiento esperado."
              />
            </div>

            {/* Period Info Banner */}
            {(analysisResult.baseline_period || analysisResult.analysis_period) && (
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                <CardContent className="py-4">
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-purple-500" />
                      <span className="text-slate-600">
                        <strong>Baseline:</strong> {analysisResult.baseline_period || "Todos los datos"}
                      </span>
                    </div>
                    <div className="h-4 w-px bg-slate-300" />
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-pink-500" />
                      <span className="text-slate-600">
                        <strong>Análisis:</strong> {analysisResult.analysis_period || "Todos los datos"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Explanation banner when there are results */}
            {analysisResult.anomalies.length > 0 && (
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-600">
                      <p className="font-medium text-slate-700 mb-1">¿Cómo leer esta tabla?</p>
                      <p>
                        Cada fila muestra un día con anomalías detectadas. Las celdas coloreadas indican métricas fuera de lo normal: 
                        <span className="inline-flex items-center mx-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs">🔴 rojo = crítico</span>
                        <span className="inline-flex items-center mx-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">🟡 amarillo = advertencia</span>. 
                        Haz clic en cualquier celda para ver el Z-Score exacto y su interpretación.
                        <button 
                          onClick={() => setShowInfoPage(true)} 
                          className="ml-2 text-purple-600 hover:underline font-medium"
                        >
                          Aprender más →
                        </button>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Anomalies Table */}
            {analysisResult.anomalies.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Anomalías Detectadas</CardTitle>
                      <CardDescription>
                        {analysisResult.anomalies.length} registros con desviaciones significativas
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={analyzeClient}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-medium uppercase">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className="flex items-center gap-1">
                                  Nivel
                                  <Info className="h-3 w-3 text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs max-w-xs">CRITICAL (🔴): Z-Score > 3. WARNING (🟡): Z-Score entre 2 y 3.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase">Cuenta</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Fecha</TableHead>
                          <TableHead className="text-xs font-medium uppercase text-center">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className="flex items-center gap-1 justify-center">
                                  Anomalías
                                  <Info className="h-3 w-3 text-slate-400" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs max-w-xs">Cantidad de métricas con Z-Score > 2 en este registro.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>Impressions</TooltipTrigger>
                                <TooltipContent><p className="text-xs">Veces que se mostraron los anuncios</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>Clicks</TooltipTrigger>
                                <TooltipContent><p className="text-xs">Clics recibidos en los anuncios</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>Conversions</TooltipTrigger>
                                <TooltipContent><p className="text-xs">Acciones valiosas completadas (compras, registros, etc.)</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>Cost (USD)</TooltipTrigger>
                                <TooltipContent><p className="text-xs">Gasto total en publicidad en dólares</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>CTR %</TooltipTrigger>
                                <TooltipContent><p className="text-xs">Click-Through Rate: Porcentaje de impresiones que generaron clic</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                          <TableHead className="text-xs font-medium uppercase">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>Conv. Rate %</TooltipTrigger>
                                <TooltipContent><p className="text-xs">Tasa de conversión: Porcentaje de clics que generaron conversión</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analysisResult.anomalies.map((anomaly, idx) => (
                          <TableRow key={idx} className={
                            anomaly.alert_level === "CRITICAL" ? "bg-red-50/50" :
                            anomaly.alert_level === "WARNING" ? "bg-amber-50/50" : ""
                          }>
                            <TableCell>
                              <Badge className={
                                anomaly.alert_level === "CRITICAL" 
                                  ? "bg-red-100 text-red-700 border-red-200" 
                                  : "bg-amber-100 text-amber-700 border-amber-200"
                              }>
                                {anomaly.alert_level === "CRITICAL" ? "🔴" : "🟡"} {anomaly.alert_level}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium text-sm max-w-[150px] truncate">
                              {anomaly.cuenta}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {anomaly.date}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{anomaly.anomaly_count}</Badge>
                            </TableCell>
                            <TableCell>
                              <MetricCell metric={anomaly.impressions} label="Impressions" />
                            </TableCell>
                            <TableCell>
                              <MetricCell metric={anomaly.clicks} label="Clicks" />
                            </TableCell>
                            <TableCell>
                              <MetricCell metric={anomaly.conversions} label="Conversions" />
                            </TableCell>
                            <TableCell>
                              <MetricCell metric={anomaly.cost} label="Cost (USD)" />
                            </TableCell>
                            <TableCell>
                              <MetricCell metric={anomaly.ctr} label="CTR %" />
                            </TableCell>
                            <TableCell>
                              <MetricCell metric={anomaly.conversion_rate} label="Conversion Rate %" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {analysisResult.anomalies.length === 0 && (
              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-emerald-700">¡Todo en orden!</h3>
                  <p className="text-emerald-600 mt-2">
                    No se detectaron anomalías significativas para {analysisResult.client_name}
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Existing Alerts Section */}
        {existingAlerts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Alertas Programadas</CardTitle>
              <CardDescription>
                Alertas de anomalías configuradas para ejecución automática
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Frecuencia</TableHead>
                    <TableHead>Notificaciones</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {existingAlerts.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell className="font-medium">{alert.alert_name}</TableCell>
                      <TableCell>{alert.client_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-slate-400" />
                          {formatSchedule(alert.schedule_interval)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {alert.notification_emails?.length > 0 ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge className={
                                  alert.notification_status === "configured"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-blue-50 text-blue-700 border-blue-200"
                                }>
                                  {alert.notification_status === "configured" ? (
                                    <><CheckCircle2 className="h-3 w-3 mr-1" />Activo</>
                                  ) : (
                                    <><Mail className="h-3 w-3 mr-1" />{alert.notification_emails.length}</>
                                  )}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs">
                                  {alert.notification_emails.map((e, i) => <div key={i}>{e}</div>)}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-slate-400 text-xs">Sin configurar</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          alert.status === "active"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }>
                          {alert.status === "active" ? "Activa" : alert.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteAlert(alert.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
